package com.tovtalk.domain.repository

import com.tovtalk.domain.entity.LoginMethod
import com.tovtalk.domain.entity.DomainResult
import com.tovtalk.domain.entity.FollowPreferences
import com.tovtalk.domain.entity.SessionInfo
import com.tovtalk.domain.entity.SuggestedArtistsResult
import com.tovtalk.domain.entity.User
import com.tovtalk.domain.entity.VerificationRequestInfo
import com.tovtalk.domain.entity.WhoCanViewPost
import kotlinx.coroutines.flow.Flow

/**
 * User Repository Interface
 */
interface UserRepository {
    
    // Authentication
    suspend fun login(
        identity: String,
        password: String?,
        loginMethod: LoginMethod,
        isHadran: Boolean = false,
        kosherLevelId: Int? = null
    ): DomainResult<User>
    
    suspend fun register(
        fullname: String,
        email: String,
        password: String,
        loginMethod: LoginMethod
    ): DomainResult<User>
    
    suspend fun logout(): DomainResult<Unit>

    /** Request guest token and store it; allows viewing feed without registration. Pass onboarding kashrut level and consumption category IDs when available. */
    suspend fun continueAsGuest(kosherLevelId: Int? = null, consumptionCategoryIds: List<Int>? = null): DomainResult<Unit>
    
    suspend fun deleteAccount(): DomainResult<Unit>
    
    // User Data
    suspend fun getUserDetails(userId: Int, forceRefresh: Boolean = false): DomainResult<User>

    suspend fun getUserByUsername(username: String, forceRefresh: Boolean = false): DomainResult<User>

    suspend fun getCurrentUser(forceRefresh: Boolean = false): DomainResult<User>
    
    suspend fun updateUserProfile(
        fullname: String? = null,
        username: String? = null,
        bio: String? = null,
        profilePhoto: ByteArray? = null
    ): DomainResult<User>
    
    suspend fun updateUserSettings(
        appLanguage: String? = null,
        whoCanViewPost: WhoCanViewPost? = null,
        showMyFollowing: Boolean? = null,
        receiveMessage: Boolean? = null,
        notifyPostLike: Boolean? = null,
        notifyPostComment: Boolean? = null,
        notifyFollow: Boolean? = null,
        notifyMention: Boolean? = null,
        notifyGiftReceived: Boolean? = null,
        notifyChat: Boolean? = null
    ): DomainResult<User>
    
    // Generic update method for any profile fields
    suspend fun updateProfile(updates: Map<String, Any>): DomainResult<User>

    // Follow
    suspend fun followUser(userId: Int): DomainResult<Unit>
    
    suspend fun unfollowUser(userId: Int): DomainResult<Unit>
    
    suspend fun getFollowers(userId: Int, page: Int): DomainResult<List<User>>
    
    suspend fun getFollowing(userId: Int, page: Int): DomainResult<List<User>>

    suspend fun getFollowPreferences(userId: Int): DomainResult<FollowPreferences>

    suspend fun updateFollowPreferences(
        userId: Int,
        notifyNewPosts: Boolean? = null,
        notifyNewStories: Boolean? = null,
        notifyLive: Boolean? = null
    ): DomainResult<FollowPreferences>
    
    /** Report a user (profile). Uses report reasons with applies_to=user. */
    suspend fun reportUser(userId: Int, reasonId: Int, description: String): DomainResult<Unit>

    // Block
    suspend fun blockUser(userId: Int): DomainResult<Unit>
    
    suspend fun unblockUser(userId: Int): DomainResult<Unit>
    
    suspend fun getBlockedUsers(limit: Int = 20, lastId: Int? = null): DomainResult<List<User>>
    
    // Search
    suspend fun searchUsers(query: String, page: Int): DomainResult<List<User>>
    suspend fun getSuggestedArtists(limit: Int = 20, offset: Int = 0): DomainResult<SuggestedArtistsResult>

    // Device Token (FCM)
    suspend fun updateDeviceToken(token: String, device: String): DomainResult<Unit>

    // Sessions
    suspend fun getSessions(): DomainResult<List<SessionInfo>>
    suspend fun deleteSession(sessionId: Int): DomainResult<Unit>
    /** Updates the current user's kashrut (viewing) level. Per-user; applies to account, not per-device. */
    suspend fun updateKashrutLevel(kosherLevelId: Int): DomainResult<Unit>
    
    // Phone Verification
    suspend fun startPhoneVerification(phone: String): DomainResult<VerificationRequestInfo>
    suspend fun getVerificationStatus(): DomainResult<VerificationRequestInfo>
    
    // Cache
    fun getCachedUser(): Flow<User?>
    
    suspend fun cacheUser(user: User)
    
    suspend fun clearCache()
}
